import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

export type PackRow = Tables<"packs">;
export type PackFileRow = Tables<"pack_files">;
export type WhatYouBuildRow = { part: string; what: string; does: string };

export const PACK_LIST_COLUMNS =
  "id, slug, title, tagline, summary, category, level, stack, tags, time_estimate, cost_line, prompts_count, version, cover_path, status, sort_order, published_at";

/** Packs with this category are listed in the Prompt Library (/prompts), not on /packs. */
export const PROMPT_PACK_CATEGORY = "Prompt Pack";

/** Public URL for a cover in the public bucket; a neutral fallback when a pack has none. */
export const coverUrl = (coverPath: string | null): string => {
  if (!coverPath) return "/images/templates/default.webp";
  return supabase.storage.from("pack-covers").getPublicUrl(coverPath).data.publicUrl;
};

/** Published packs, newest first. Used by /packs and by "more packs" on a detail page. */
export const usePacks = () =>
  useQuery({
    queryKey: ["packs", "published"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("packs")
        .select(PACK_LIST_COLUMNS)
        .eq("status", "published")
        .order("sort_order", { ascending: true })
        .order("published_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Omit<PackRow, "body_md" | "created_by" | "created_at" | "updated_at">[];
    },
    staleTime: 60_000,
  });

/** One pack with its files. RLS decides whether a draft is visible (admins only). */
export const usePack = (slug: string | undefined) =>
  useQuery({
    queryKey: ["packs", "one", slug],
    enabled: !!slug,
    queryFn: async () => {
      const { data: pack, error } = await supabase.from("packs").select("*").eq("slug", slug!).maybeSingle();
      if (error) throw error;
      if (!pack) return null;
      const { data: files, error: fErr } = await supabase
        .from("pack_files")
        .select("*")
        .eq("pack_id", pack.id)
        .order("sort_order", { ascending: true });
      if (fErr) throw fErr;
      return { pack: pack as PackRow, files: (files ?? []) as PackFileRow[] };
    },
  });

/**
 * Mint a short-lived URL for a file in the private bucket. Succeeds only when the
 * storage select policy passes for the signed-in user (member or admin); anyone else
 * gets an error, which the page shows as the lock box.
 */
export const signedPackFileUrl = async (storagePath: string, seconds = 120): Promise<string> => {
  const { data, error } = await supabase.storage.from("packs").createSignedUrl(storagePath, seconds);
  if (error || !data?.signedUrl) throw error ?? new Error("Could not sign the file URL");
  return data.signedUrl;
};

export const formatBytes = (n: number | null): string => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export const LEVEL_LABEL: Record<string, string> = {
  starter: "Starter",
  intermediate: "Intermediate",
  advanced: "Advanced",
};
